import type { Campaign } from "@campaign/domain/models/campaign.model";
import { Campaigns } from "@campaign/domain/models/campaign.model";
import { CampaignDeleteUseCase } from "@campaign/domain/usecases/campaign-delete.usecase";
import { CampaignGetAllUseCase } from "@campaign/domain/usecases/campaign-get-all.usecase";
import { ToastManagerRepository } from "@core/domain/repositories/toast-manager.repository";
import { LoadLayoutStore } from "@core/presentation/components/feedback/load-layout/load-layout.store";
import { BaseViewModel } from "@iot-platform/core";
import { inject, injectable } from "inversify";
import { action, makeObservable, observable, runInAction } from "mobx";

@injectable()
export class HistoricalCampaignComponentViewmodel extends BaseViewModel {
    @observable
    campaignsLoading = true;

    @observable
    campaigns: Campaigns = Campaigns.empty();

    constructor(
        @inject(CampaignGetAllUseCase)
        private readonly campaignGetAllUseCase: CampaignGetAllUseCase,
        @inject(CampaignDeleteUseCase)
        private readonly campaignDeleteUseCase: CampaignDeleteUseCase,
    ) {
        super();
        makeObservable(this);
    }

    @action
    setPagination(page: number, perPage: number): void {
        const result = this.campaigns.updatePagination(page, perPage);

        if (result.isOk()) {
            this.campaigns = result.value;
            void this.fetchCampaigns();
        }
    }

    @action
    async fetchCampaigns(): Promise<void> {
        this.campaignsLoading = true;

        const campaigns = await this.campaignGetAllUseCase.execute(
            this.campaigns.pagination,
        );

        runInAction(() => {
            this.campaigns = campaigns;
            this.campaignsLoading = false;
        });
    }

    async deleteCampaign(campaignId: Campaign["id"]): Promise<void> {
        LoadLayoutStore.start();

        const deletedCampaign =
            await this.campaignDeleteUseCase.execute(campaignId);

        if (deletedCampaign) {
            ToastManagerRepository.success();
            await this.reloadCampaigns();
        }

        LoadLayoutStore.finish();
    }

    @action
    setCampaigns(campaigns: Campaigns): void {
        runInAction(() => {
            this.campaigns = campaigns;
            this.campaignsLoading = false;
        });
    }

    async reloadCampaigns(): Promise<void> {
        LoadLayoutStore.start();
        await this.fetchCampaigns();
        LoadLayoutStore.finish();
    }
}
